/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps, RefObject, JSX } from 'preact';
import { useMemo, useRef } from 'preact/hooks';
import type { Dispatch } from 'preact/hooks';

import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';

import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { useUser } from '#hooks/UNSAFE_useUser';
import type { BundleType } from '#resources/nls/bundle';
import { Flex } from '#UNSAFE_Flex';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { LiveRegion } from '#UNSAFE_LiveRegion';
import { TextFieldInputRedwoodTheme } from '#UNSAFE_TextField/themes/redwood/TextFieldInputTheme';
import type { TextFieldInputVariantOptions } from '#UNSAFE_TextField/themes/TextFieldInputStyles.css';
import { isEmbeddedVariant } from '#utils/PRIVATE_formControlUtils';
import { formatFullCalendarDate } from '#utils/UNSAFE_calendarDateUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { DateSegment } from './DateSegment';
import type { InputDateMask } from './InputDateMask';
import { LiteralSegment } from './LiteralSegment';
import type { DatePlaceholders, EditableDateSegment } from './types';
import { type DateFieldState } from './getDateFieldInitialReducerState';
import { useDateFieldHandlers } from './useDateFieldHandlers';
import type { DateReducerAction } from './dateReducer';
import { mergeDerivedStateWithReducerState } from './mergeDerivedStateWithReducerState';

type InputDateMaskProps = ComponentProps<typeof InputDateMask>;
type mergeDerivedStateWithReducerStateReturnType = ReturnType<
  typeof mergeDerivedStateWithReducerState
>;
type SegmentsType = mergeDerivedStateWithReducerStateReturnType['segments'][number];

type AriaProps = Pick<
  UnpackSignals<JSX.AriaAttributes>,
  'aria-describedby' | 'aria-invalid' | 'aria-label' | 'aria-labelledby'
>;

type HTMLElementProps = Pick<UnpackSignals<JSX.IntrinsicElements['div']>, 'onBlur' | 'onFocus'>;

export type Props = AriaProps &
  HTMLElementProps & {
    /**
     * A base id prefix.
     */
    baseId: string;

    /**
     * The reducer's dispatch function. The dispatch function lets you update the state to a different value and trigger a re-render.
     */
    dispatch: Dispatch<DateReducerAction>;

    /**
     * A ref to the element that receives focus when the field's label is clicked,
     * or when the component is toggled between readonly and enabled. This is the
     * first segment in the field.
     */
    fieldRef?: RefObject<HTMLDivElement>;

    /**
     * Specifies the smallest date unit that is displayed by the component.
     * If set to month, only month and year are shown.
     * If set to day then month, day, and year are shown.
     */
    granularity?: InputDateMaskProps['granularity'];

    /**
     * True if the component has an inside label.
     */
    hasInsideLabel?: boolean;

    /**
     * Specifies for accessibility purposes whether a value is required.
     */
    isRequired?: InputDateMaskProps['isRequired'];

    /**
     * The placeholder masks used by the date field.
     */
    masks: DatePlaceholders;

    /**
     * The state for the DateField.
     */
    state: DateFieldState;

    /**
     * Specifies how to align text within the field.
     */
    textAlign?: InputDateMaskProps['textAlign'];

    /**
     * The current display value of the component.
     */
    value?: InputDateMaskProps['value'];

    /**
     * The style variant of the component.
     */
    variant?: InputDateMaskProps['variant'];

    /**
     * Callback invoked each time the user changes the value of a segment For example, if the user
     * types '12' into the month segment, this callback will be called twice to change the month
     * first to '1' and then '12'.
     */
    onInput: InputDateMaskProps['onInput'];

    /**
     * Callback invoked when the user commits the entered value by blurring or hitting the enter key.
     */
    onCommit: InputDateMaskProps['onCommit'];
  };

const findSegment = (segments: SegmentsType[], type: EditableDateSegment) => {
  return segments.find((segment) => segment.type === type)!;
};

/**
 * Container for Segment children that represent different parts of a date with no time.
 */
const DateField = ({
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  baseId,
  dispatch,
  fieldRef,
  granularity = 'day',
  hasInsideLabel,
  isRequired,
  masks: dateMasks,
  onBlur,
  onCommit,
  onFocus,
  onInput,
  state,
  textAlign,
  value,
  variant = 'default'
}: Props) => {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const announceDateCleared = translations.inputDateMask_date_cleared();

  const { direction, locale } = useUser();

  const { isDisabled, isFocused, isInputFocused } = useFormFieldContext();
  const hasValue = value !== undefined;

  // DateField is similar to TextFieldInput for styling. The main difference
  // is TextFieldInput renders a textarea or input and DateField renders a div, so the positioning with
  // padding is different. DateField and TextFieldInput are both the mainContent
  // of a TextField component.
  const { classes } = useComponentTheme<TextFieldInputVariantOptions>(TextFieldInputRedwoodTheme, {
    type: 'notPassword',
    styleVariant: isEmbeddedVariant(variant) ? 'embedded' : 'default',
    textarea: 'notTextArea',
    input: 'notInput',
    prefix: 'noPrefix',
    suffix: 'noSuffix',
    startContent: 'noStartContent',
    endContent: 'noEndContent',
    insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
    value: hasValue ? 'hasValue' : 'noValue',
    focused: isFocused ? 'isFocused' : 'notFocused',
    disabled: isDisabled ? 'isDisabled' : 'notDisabled'
  });

  const compClasses = classNames([classes]);

  const ariaLabels = useMemo(
    () => ({
      month: translations.formControl_month(),
      day: translations.formControl_day(),
      year: translations.formControl_year()
    }),
    [translations]
  );

  // On every render we figure out information we need to render.
  // And we merge in the state we are keeping like what segment is selected.
  const {
    isCompleteDate,
    isDateSelected,
    isPartialDate,
    segments: editableSegments
  } = mergeDerivedStateWithReducerState({
    currentState: state,
    ariaLabels,
    granularity,
    locale,
    value
  });

  // Construct an ordered list of the editable segments, i.e. ['month', 'day', 'year'].
  // Figure this out from the dateMasks
  const orderedSegments = useMemo(() => {
    const fdm = dateMasks.filter(({ type }) => type !== 'literal');
    return fdm.map(({ type }) => type) as EditableDateSegment[];
  }, [dateMasks]);

  const yearSegment = findSegment(editableSegments, 'year');
  const monthSegment = findSegment(editableSegments, 'month');
  const daySegment = granularity === 'day' ? findSegment(editableSegments, 'day') : undefined;
  const segmentsMap = {
    year: yearSegment,
    month: monthSegment,
    day: daySegment
  };

  const groupRef = useRef<HTMLDivElement>(null);

  const hasSelectedSegment = editableSegments.some((segment) => !!segment.isSelected);
  const { dateClearedRef, dateFieldHandlers, segmentHandlers, tabIndex } = useDateFieldHandlers({
    hasSelectedSegment,
    value,
    direction,
    dispatch,
    groupRef,
    isCompleteDate,
    isDateSelected,
    isDisabled,
    isInputFocused,
    orderedSegments,
    state,
    onCommit,
    onInput
  });

  // Like other form components, don't show placeholders for inside labels unless focused.
  // This flag causes the segment placeholders to be hidden.
  const isHidden = value === undefined && hasInsideLabel && !isFocused;

  // If the date is complete, create a hidden accessible region with the full formatted date.
  const formattedDateStr = !isCompleteDate
    ? ''
    : formatFullCalendarDate(locale, value!, granularity);

  const formattedDateAriaId = `${baseId}-date`;
  const formattedDateAria = isCompleteDate ? (
    <HiddenAccessible id={formattedDateAriaId}>{formattedDateStr}</HiddenAccessible>
  ) : undefined;
  const ariaDescribedBy = isCompleteDate ? merge([describedBy, formattedDateAriaId]) : describedBy;

  // Add an announcement to the live region if the date was cleared.
  // Note: we deliberately do not reset dateClearedRef because there are multiple
  // rerenders after clearing the date and we want to make sure the announcement
  // has time to be heard.
  const announceText = dateClearedRef.current ? announceDateCleared : '';

  // firstSegmentType is 'day', 'month', or 'year' and is used to determine
  // where to put the fieldRef.
  const firstSegmentType = orderedSegments[0];

  return (
    <div
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      class={compClasses}
      onFocusIn={onFocus}
      onFocusOut={onBlur}
      ref={groupRef}
      role="group"
      tabIndex={tabIndex}
      {...dateFieldHandlers}>
      <Flex direction="row" wrap="nowrap" justify={textAlign} height="100%" align="center">
        {dateMasks.map(({ type, value: placeholder }) => {
          if (type === 'literal') {
            return (
              <LiteralSegment
                isHidden={isHidden}
                isHighlighted={isDateSelected}
                isPlaceholder={!isPartialDate}
                text={placeholder}></LiteralSegment>
            );
          } else {
            const segmentProps = segmentsMap[type];
            if (segmentProps) {
              return (
                <DateSegment
                  inputRef={type === firstSegmentType ? fieldRef : undefined}
                  isDisabled={isDisabled}
                  isFieldFocused={isFocused}
                  isInvalid={!!ariaInvalid}
                  isHidden={isHidden}
                  isHighlighted={isDateSelected}
                  isRequired={isRequired}
                  orderedSegments={orderedSegments}
                  placeholder={placeholder}
                  {...segmentHandlers}
                  {...segmentProps}></DateSegment>
              );
            }
            return null;
          }
        })}
      </Flex>
      {formattedDateAria}
      <LiveRegion>{announceText}</LiveRegion>
    </div>
  );
};

export { DateField, findSegment };
